import Dropzone from 'dropzone'
import { queryParent, isIOS } from './tools'
import Sortable from 'sortablejs/modular/sortable.complete.esm.js';
import i18next from 'i18next';
import * as Sentry from '@sentry/browser';
import { beforeSubmit } from "./required_input_validator"

// 禁止對所有元素的自動查找:
Dropzone.autoDiscover = false

function postFileGroupSettings(groupId, maxFilesizeMb) {
  return {
    method: 'PUT',
    url: '/',
    presign_upload_url: `/post_files/presign_upload`,
    paramName: 'attachments[files]',
    maxFilesize: maxFilesizeMb, // MB，來自 data-max-filesize（Settings），不在前端寫死
    // 單檔超過上限時 Dropzone 自己擋（file.accepted 為 false），訊息跟 presign 422 同一句設計師語言
    dictFileTooBig: i18next.t('models.file_group_fields.presign_file_too_large', { limit: Math.floor(maxFilesizeMb / 1024) }),
    parallelUploads: 2,
    file_group_id: groupId,
    fileIDs: []
  }
}

function postImgSettings() {
  return {
    method: 'PUT',
    url: '/',
    presign_upload_url: `/post_images/presign_upload`,
    paramName: 'attachments[images]',
    maxFilesize: 50, // MB
    parallelUploads: 2,
    acceptedFiles: 'image/jpeg,image/png,image/jpg,image/gif',
    file_group_id: '',
    fileIDs: []
  }
}

function campaignImgSettings() {
  return {
    method: 'PUT',
    url: '/',
    presign_upload_url: `/campaign_images/presign_upload`,
    paramName: 'attachments[images]',
    maxFilesize: 50, // MB
    parallelUploads: 2,
    acceptedFiles: 'image/jpeg,image/png,image/jpg,image/gif',
    file_group_id: '',
    fileIDs: []
  }
}

function removeLink(dropzone, fileName, fileId, form) {
  let url = ''
  const fileExtension = fileName.substring(fileName.lastIndexOf(".") + 1)

  if(['jpeg','png','jpg','gif'].includes(fileExtension.toLowerCase())) {
    if(dropzone.type == 'CampaignImage') {
      url = `/campaign_images/${fileId}`
    } else {
      url = `/post_images/${fileId}`
    }
  } else {
    url = `/post_files/${fileId}`
  }

  fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
    }
  })
    .then(response => {
      // 檢查必填欄位
      beforeSubmit(form)
    })
    .catch(error => {
      console.error('Remove image Error:', error);
    });
}

// presign 被伺服器拒絕（非 2xx）；message 是回應帶的、已是使用者語言的原因（可能為空）
class PresignError extends Error {}

function getUploadLink(dropzone, file, objectID, callback, onError) {
  const options = dropzone.dropZone.options
  const filename = encodeURIComponent(file.name)

  var url
  if(dropzone.type == 'CampaignImage') {
    url = `${options.presign_upload_url}?filename=${filename}&file_size=${file.size}&campaign_id=${objectID}`
  } else {
    url = `${options.presign_upload_url}?filename=${filename}&file_size=${file.size}&file_group_id=${options.file_group_id}&post_id=${objectID}`
  }

  fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
    }
  })
    .then(response => {
      if (response.ok) { return response.json() }

      // 被拒（例如單檔／檔案群組超過上限，422）：回應的 message 已經是使用者語言
      return response.json().catch(() => ({})).then(body => {
        throw new PresignError(body.message)
      })
    })
    .then(res => {
      callback({
        presignedUrl: res.presigned_url,
        id: res.attachment_id
      })
    })
    .catch(error => {
      console.error('GetUploadLink error:', error);
      onError(error instanceof PresignError && error.message ? error.message : i18next.t('models.file_group_fields.presign_failed'))
    });
};

function uploadFileSuccess(dropzone, fileName, fileId, callback) {
  let url = ''
  const fileExtension = fileName.substring(fileName.lastIndexOf(".") + 1)

  if(['jpeg','png','jpg','gif'].includes(fileExtension.toLowerCase())) {
    if(dropzone.type == 'CampaignImage') {
      url = `/campaign_images/${fileId}/upload_successed`
    } else {
      url = `/post_images/${fileId}/upload_successed`
    }
  } else {
    url = `/post_files/${fileId}/upload_successed`
  }

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
    }
  })
    .then(response => response.json())
    .then(body => {
      callback(body)
    })
    .catch(error => {
      console.error('UploadFileSuccess error:', error);
    });
}

// upload_successed 只在「這個 zip 真的會被解壓管線處理」時才帶 unzip_pending
// （見 attachments_controller.rb：沒開 lambda 時完全不帶這個欄位），所以這裡不用
// 另外判斷 lambda 有沒有部署——直接信任後端旗標即可，兩種情境天然分岔。
function isUnzipPending(uploadResult) {
  return !!(uploadResult && uploadResult.unzip_pending)
}

function markDropzoneFileAsUnzipping(file) {
  if (!file.previewElement) return

  file.previewElement.classList.add('dz-unzipping')

  const label = file.previewElement.querySelector('[data-dz-unziplabel]')
  if (label) {
    label.textContent = i18next.t('models.file_group_fields.unzip_pending')
  }
}

function addFileIdToSelect(self, fileId) {
  const existingOptionIds = Array.from(self.$select.querySelectorAll('option')).map(node => node.value)

  if (!existingOptionIds.includes(String(fileId))) {
    const fileOption = self._createOption({
      value: fileId,
      selected: 'selected'
    })
    self.$select.appendChild(fileOption)
  }
}

function checkUploadState() {
  const allProgress = document.querySelectorAll("[data-dz-uploadprogress]")
  let uploadSuccess = true

  for(let i = 0; i < allProgress.length; i++) {
    let progressWidth = allProgress[i].style.width

    if (progressWidth == '' || progressWidth == '100%'){
      uploadSuccess = uploadSuccess && true
    } else {
      uploadSuccess = uploadSuccess && false
    }
  }

  if (uploadSuccess) {
    document.body.dataset.dropzoneUploading = false
  }
}

const DROPZONE_SELECTOR = '[data-dropzone]'

// with dropzone_component.html.erb
class DropZoneClass {
  constructor($dom){
    this.$dom = $dom
    this.$form = queryParent('form')(this.$dom)
    this.type = this.$dom.dataset['dropzone'] // 代表dropzone的type, 如 postFileGroup, postImage
    this.$select = this.$dom.querySelector('select')
    this.hint = this.$dom.parentNode.querySelector('.dropzone-hint')
    this.errorMsg = $(this.$dom.parentNode).siblings('.dropzone-error-msg')[0]
    this.acceptFormat = ''
    this.disabled = this.$dom.dataset.disabled
    this.maxFiles = this.$dom.dataset.maxFiles
    this.maxFilesize = Number(this.$dom.dataset.maxFilesize)

    if (!this.type) {
      throw 'data-dropzone is no type!'
    }

    if (this.type == 'postFileGroup' ) {
      const group_id = this.$dom.dataset['fileGroupId']
      this.settings = postFileGroupSettings(group_id, this.maxFilesize) || {}

      // from _file_group_fields
      this.acceptFormat = this.$dom.dataset['formats']
      // 注意除了settings.acceptedFiles外，addedfiles 也會檢查檔案格式
      // ios file input accept 有bug當限制多種file input會全部都無法上傳
      if(isIOS()) {
        this.settings.acceptedFiles = ''
      } else {
        this.settings.acceptedFiles = this.acceptFormat
      }
    } else if (this.type == 'CampaignImage') {
      this.settings = campaignImgSettings() || {}
      this.acceptFormat = this.settings.acceptedFiles
    } else {
      // post img group 限制從settings上取得
      this.settings = postImgSettings() || {}
      this.acceptFormat = this.settings.acceptedFiles
    }

    if (this.$form.action.match(/\/profile\/models\/(\d+)/)){
      this.objectID = this.$form.action.match(/\/profile\/models\/(\d+)/)[1]
    } else if (this.$form.action.match(/\/profile\/campaigns\/(\d+)/)){
      this.objectID = this.$form.action.match(/\/profile\/campaigns\/(\d+)/)[1]
    }

    this.isEditForm = this._checkEditForm()
    this.init()
  }

  init(){
    this.dropZone = this._createDropZone();
  }

  destroy(){
    this.dropZone.destroy()
  }

  _dropzoneSettings() {
    return {
      autoProcessQueue: true,
      autoQueue: false,
      dictDefaultMessage: '',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      previewTemplate: document.querySelector(".my-template").innerHTML,
      ...this.settings
    }
  }

  _checkEditForm() {
    const formUrl = new URL(this.$form.action)
    return formUrl.pathname.match(/\/\d+(|\/file_groups|\/adv_update)$/)
  }

  _createOption({value, selected}) {
    const option = document.createElement('option')
    option.setAttribute('value', value)
    option.setAttribute('selected', selected)
    return option
  }

  _createSortedInput({self, file}) {
    if (!self.type.match(/image$/i)) {
      return
    }

    const selectName = self.$select.name // ex: post[image_ids][]
    const originParams = selectName.match(/\[.*_ids\]/)[0].slice(1,-1) // ex: image_ids
    const sortedParams = "sorted_" + originParams // ex: sorted_image_ids
    const sortedName = selectName.replace(originParams, sortedParams)

    const checkbox = document.createElement('input')
    checkbox.setAttribute('type', 'checkbox')
    checkbox.setAttribute('name', sortedName)
    checkbox.setAttribute('value', file.id)
    checkbox.setAttribute('checked', 'checked')
    checkbox.setAttribute('hidden', 'hidden')

    file.previewElement.appendChild(checkbox)
  }

  async _fetchFiles() {
    var url = ''
    if (this.type == 'CampaignImage') {
      url = `/api/v1/campaigns/${this.objectID}/attachments`
    } else {
      url = `/api/v1/posts/${this.objectID}/attachments`
    }

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8' },
      method: 'get'
    })
    const json = await response.json()
    return json
  }

  // file.dataURL 是原圖的 base64。後端打 Lambda 的 payload 上限 6 MB，
  // 原圖全送會超限（多張或大圖），所以這裡刻意只取前幾張並縮圖再送。
  // 張數上限要與 Ai::RecommendHashtagService::MAX_IMAGES 一致。
  downscaleDataURL(dataURL, maxEdge = 1024, quality = 0.8) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height))
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * ratio)
        canvas.height = Math.round(img.height * ratio)
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', quality))
      }
      img.onerror = reject
      img.src = dataURL
    })
  }

  async recommend_hashtag(files) {
    var formData = new FormData();
    const images = await Promise.all(
      files.slice(0, 3).map(file => this.downscaleDataURL(file.dataURL))
    )
    images.forEach(image => {
      formData.append('images[]', image);
    });
    const response = await fetch('/api/v1/posts/recommend_hashtags', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: formData
    })
    if (!response.ok) return { tags: [] }
    const json = await response.json()
    return json
  }

  isImage(type) {
    return /image\/(jpeg|png|jpg|gif)/.test(type)
  }

  isAiImage(type) {
    return /image\/(jpeg|png|jpg)/.test(type)
  }

  _createDropZone(){
    const self = this

    if (self.type.match(/image$/i)) {
      new Sortable(this.$dom, {
        animation: 150,
        swapThreshold: 1,
      });
    }

    return new Dropzone(this.$dom, {
      ...this._dropzoneSettings(),

      init: async function() {
        var myDropzone = this
        let isDragger = false
        let has_recommend_hashtag = false

        if (self.isEditForm) {
          // get existing files data
          const data = await self._fetchFiles()
          var data_array = []

          if (data['files'] == null && data['images'] == null) {
            return
          }

          if (self.type.match(/image$/i)) {
            data_array = data['images']
          } else {
            data_array = data['files']
          }

          // display existing files
          const filter_data_array = data_array.filter( a => String(a.file_group_id) === this.options.file_group_id )

          if (filter_data_array.length > 0){
            self.hint.classList.add('hidden')
            beforeSubmit(self.$form)
          }

          filter_data_array.forEach( file => {
            var file_path = file.url
            myDropzone.files.push(file)
            myDropzone.options.addedfile.call(myDropzone, file)
            myDropzone.options.processing.call(myDropzone, file)
            if (self.type.match(/image$/i)) {
              myDropzone.options.thumbnail.call(myDropzone, file, file_path)
            }
            myDropzone.options.complete.call(myDropzone, file)

            // 回訪時 zip 可能還在等 lambda 解壓（見 /attachments 的 unzip_pending），
            // 補回跟剛上傳時一樣的「解壓縮中」badge，蓋掉預設的完成勾勾
            if (file.unzip_pending) {
              markDropzoneFileAsUnzipping(file)
            }

            self._createSortedInput({ self: self, file: file })
          })

          // change accepted file types
          const fileTypeEleName = self.$select.id.replace('draft_file_ids', 'file_type')
          const fileTypeSelect = document.getElementById(fileTypeEleName)

          fileTypeSelect.addEventListener('change', function(){
            // view _file_group_fields
            const displayTypes = this.options[this.selectedIndex].dataset['display'];
            // dropzone_conponent.html
            const hintFormats = self.hint.querySelector('.avaliable-formats')
            hintFormats.dataset['display'] = displayTypes
            hintFormats.innerHTML = i18next.t('models.base_form.available_formats') + ' : ' + displayTypes

            const displayCtbTip = this.options[this.selectedIndex].dataset['ctbTip'];
            const ctbTip = self.hint.querySelector('.ctb-tip')

            if (displayCtbTip == 'true') {
              ctbTip.classList.remove('hidden')
            } else {
              ctbTip.classList.add('hidden')
            }

            // addedfiles 不能使用 this.options.acceptedFiles 取得驗證格式
            self.acceptFormat = this.options[this.selectedIndex].dataset['formats'];
            myDropzone.options.acceptedFiles = self.acceptFormat;

            // ios file input accept 有bug當限制多種file input會全部都無法上傳
            if(isIOS()) {
              myDropzone.hiddenFileInput.accept = '';
            } else {
              myDropzone.hiddenFileInput.accept = self.acceptFormat;
            }
          })

          if (self.disabled == 'true') {
            myDropzone.disable()
          }
        }

        this.on("sending", function(file, xhr, formData) {
          if(self.isImage(file.type)) {
            var _send = xhr.send;
            xhr.send = function() {
              myDropzone.createThumbnail(
                file,
                myDropzone.options.thumbnailWidth,
                myDropzone.options.thumbnailHeight,
                myDropzone.options.thumbnailMethod,
                false,
                function(dataURL) {
                  // Update the Dropzone file thumbnail
                  myDropzone.emit('thumbnail', file, dataURL);
                }
              );
              _send.call(xhr, file);
            }
          } else {
            var _send = xhr.send;
            xhr.send = function() {
              _send.call(xhr, file);
            }
          }
        });

        this.on('successmultiple', function(files, response) {
          const fileIDs = response.fileIDs || this.options.fileIDs

          if(fileIDs) {
            const existingOptionIds = Array.from(self.$select.querySelectorAll('option')).map(node => node.value)
            fileIDs.forEach(id => {
              if (!existingOptionIds.includes(String(id))) {
                const fileOption = self._createOption({
                  value: id,
                  selected: 'selected'
                })
                self.$select.appendChild(fileOption)
              }
            })
          }
        })

        this.on("processing", function(file) {
          if(file.presignedUrl) {
            this.options.headers['Content-Type'] = file.type
            this.options.url = file.presignedUrl;
          }
          if(file.id) {
            this.options.fileIDs.push(file.id)
            self._createSortedInput({ self: self, file: file })
          }

          document.body.dataset.dropzoneUploading = true
        });

        this.on('success', function(file, response) {
          uploadFileSuccess(self, file.name, file.id, async (uploadResult) => {
            // zip 還在等 lambda 解壓：先不把它塞進檔案選單（這個 id 幾分鐘後就會
            // 因為 discard 失效），改顯示「解壓縮中」徽章。沒有解壓管線（enabled?
            // false）或不是 zip 時，uploadResult 不會帶這個旗標，走原本的邏輯。
            if (isUnzipPending(uploadResult)) {
              markDropzoneFileAsUnzipping(file)
            } else if (file.id) {
              addFileIdToSelect(self, file.id)
            }

            // 檢查必填欄位：要在 select 更新後才驗證，否則最後一個檔案上傳完會
            // 誤判必填欄位未填、存檔鍵永遠停用
            beforeSubmit(self.$form)

            checkUploadState()
            // 圖片 ai suggest tags
            // 除了gif以外的圖片都會上傳給ai做辨識, 等到所有非gif圖片上傳成功後才會執行
            const upload_ai_images = myDropzone.files.filter(f => self.isAiImage(f.type))
            const upload_successed_ai_images = myDropzone.files.filter(f => self.isAiImage(f.type) && f.status === 'success')

            if(self.type === 'postImage' && !has_recommend_hashtag && upload_ai_images.length === upload_successed_ai_images.length) {
              has_recommend_hashtag = true
              try {
                const { tags } = await self.recommend_hashtag(upload_ai_images)

                // get tag list tomselect instance
                const tagListElement = document.querySelector('[name="post[tag_list]"]').tomselect
                if (tagListElement) {
                  tags.forEach(tag => {
                    // ai suggest tag style
                    tagListElement.addOption({ value: tag, text: tag, style: 'ai' })
                    tagListElement.addItem(tag)
                  })
                }
              } catch (err) {
                Sentry.captureException(`Ai tag api error: ${err}`);
              }
            }
          })

          if (self.errorMsg){
            self.errorMsg.classList.add('hidden')
          }
        });

        this.on('error', function(file, errorMessage){
          console.error(errorMessage)
          alert(errorMessage)
        })

        this.on("dragenter", function() {
          if (!isDragger) {
            myDropzone.element.classList.add("dz-drag-hover");
            isDragger = true;
          }
        });

        this.on("dragleave", function() {
          if (isDragger) {
            myDropzone.element.classList.remove("dz-drag-hover");
            isDragger = false;
          }
        });

        this.on("drop", function() {
          if (isDragger) {
            myDropzone.element.classList.remove("dz-drag-hover");
            isDragger = false;
          }
        });
      },

      addedfiles: function(files) {
        if(this.files.length <= self.maxFiles) {
          if (this.files.length > 0){
            self.hint.classList.add('hidden')
          }
          files.forEach(file => {
            var fileName = file.name
            var fileExtension = fileName.split('.').pop().toLowerCase();

            // 已被 Dropzone 擋下（如超過 maxFilesize）：它已經 emit 過 error，不要再送 presign 吃第二次錯誤
            if (file.accepted === false) {
              return
            }

            // 除了html input accept外，這邊也會檢查檔案格式
            if (self.acceptFormat.indexOf(fileExtension) === -1){
              alert(`Only accept ${self.acceptFormat} file`)
              return
            }
            getUploadLink(self, file, self.objectID, (data) => {
              file.presignedUrl = data.presignedUrl
              file.id = data.id
              this.enqueueFile(file)
            }, (message) => {
              // presign 失敗：把這個檔標成錯誤，不再永遠停在上傳中；同一批其他檔不受影響
              file.status = Dropzone.ERROR
              this.emit('error', file, message)
              this.emit('complete', file)
            })
          })
        } else {
          alert(`Exceed max files: ${self.maxFiles}`)
          files.forEach(file => {
            this.removeFile(file)
          })
        }
      },

      removedfile: function(file) {
        //remove dropzone previewElement
        var previewDropzone = file.previewElement
        previewDropzone.parentNode.removeChild(file.previewElement)

        if (this.files.length < 1){
          self.hint.classList.remove('hidden')
        }

        // 判斷是 show 回傳的 json 還是 dropzone 產生的
        if (file.id == null && file.xhr == null) { return }

        if (file.id){
          var fileId = file.id
        }else{
          var fileId = JSON.parse(file.xhr.responseText).fileIDs[0]
        }

        removeLink(self, file.name, file.id, self.$form)

        // remove select option
        const option = self.$select.querySelector(`option[value="${fileId}"]`)
        if (option) {
          option.remove()
        }

        const idx = this.options.fileIDs.indexOf(fileId)
        if (idx > -1) {
          this.options.fileIDs.splice(idx, 1)
        }
      },
    })
  }
}

let instances = []

export const initDropZone = () => {
  document.querySelectorAll(DROPZONE_SELECTOR).forEach(($dom) => {
    instances.push(new DropZoneClass($dom))
  })
  // select form dropzone item
  // #post_image_ids
  // #post_file_ids (簡易上傳 models/_form.html.erb)
  // #post_draft_file_ids (進階上傳 models/_file_group_fields.html.erb)
  // #campaign_image_ids (募資上傳 campaign_models/_base_form.html.erb)
  document.querySelectorAll('#post_image_ids > option, #post_file_ids > option, #post_draft_file_ids > option, #campaign_image_ids > option').forEach(dom => {
    dom.setAttribute('selected', 'selected')
  })
}

export const destroyDropZone = () => {
  instances.forEach((instance) => {
    instance.destroy()
  })
}
